/**
 * @file ReprendreSportApresAccident.tsx
 * @summary Article de blog — ReprendreSportApresAccident
 *
 * Page d'article TSX du silo SEO/GEO. Inclut métadonnées (SEO), JSON-LD, et CTA Resawod. Inscriptions cours via Resawod uniquement (sauf séances d'essai).
 *
 * @see docs/07-CODEBASE.md — conventions de code
 * @see docs/02-FRONTEND.md — design system (tokens HSL, typographie)
 */
import { Link } from "react-router-dom";
import ArticleLayout, {
  EnBref, QuandConsulter, AdaptationTable, FAQList, FurtherReading, SourcesSection, buildArticleJsonLd,
} from "@/components/blog/BlogParts";

const URL = "https://crossfitchistera.fr/blog/reprendre-sport-apres-accident";
const TITLE = "Reprendre le sport après un accident : choc, fracture, traumatisme";
const DESC = "Accident de la route, chute, fracture : comment reprendre le sport sans rechute. Étapes, critères mesurables et accompagnement coach + kiné.";

const author = { name: "Anthony Bendahmane (Benbou)", role: "Master STAPS, CrossFit Level 1" };
const reviewer = { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" };

const faq = [
  { q: "Faut-il attendre la fin de la rééducation pour reprendre ?", a: "Non, on peut souvent reprendre un travail global adapté en parallèle de la kiné, sur des zones non touchées. Cela accélère même la récupération globale." },
  { q: "Et le côté psychologique ?", a: "Souvent sous-estimé. La peur du mouvement (kinésiophobie) est réelle après un accident. Reprendre dans un cadre rassurant, avec un coach et des progressions claires, fait partie du soin." },
  { q: "Combien de temps avant un retour normal ?", a: "Très variable. Comptez 2 à 4 fois la durée d'immobilisation pour un retour complet, sans précipiter." },
  { q: "J'ai des séquelles permanentes, puis-je quand même m'inscrire ?", a: "Oui. Énormément de mouvements existent, et nos coachs adaptent. L'objectif est de retrouver de la santé et du plaisir, pas une performance avant/après." },
];

export default function ReprendreSportApresAccident() {
  return (
    <ArticleLayout
      url={URL} title={TITLE} description={DESC}
      h1="Reprendre le sport après un accident"
      breadcrumbLabel="Après un accident"
      category="Reprise · Traumatologie"
      author={author} reviewer={reviewer}
      publishedAt="2026-12-20" readingMinutes={10}
      ctaVariant="reprise" needsHealthDisclaimer
      jsonLd={buildArticleJsonLd({ url: URL, title: TITLE, description: DESC, publishedAt: "2026-12-20", author, reviewer, faq })}
    >
      <EnBref>
        <p>Après un accident — accident de la route, chute, fracture — la reprise du sport demande de la <strong>patience et un cadre clair</strong>. Le risque ne vient pas seulement de la zone blessée : c'est tout l'organisme qui s'est déconditionné, et la peur du mouvement s'installe vite. Reprendre encadré, avec des étapes mesurables, change tout.</p>
      </EnBref>

      <QuandConsulter title="Avant de reprendre une activité comme la box">
        <ul>
          <li>Validation médicale écrite (médecin traitant, chirurgien si opération)</li>
          <li>Bilan kiné si rééducation en cours, avec amplitudes et charges autorisées</li>
          <li>Absence de douleur au repos, gonflement maîtrisé</li>
          <li>Capacité à effectuer 30 min de marche soutenue sans gêne (test de base)</li>
        </ul>
      </QuandConsulter>

      <h2>Les principes de la reprise</h2>
      <ol>
        <li><strong>Réveiller progressivement.</strong> 2 à 3 séances par semaine de 45 à 60 min, intensité modérée pendant les 3 à 4 premières semaines.</li>
        <li><strong>Soigner les bases.</strong> Mobilité, gainage, posture, respiration : avant de courir et de soulever lourd.</li>
        <li><strong>Mesurer les progrès.</strong> Force, amplitude, douleur (échelle 0 à 10) — on note, on ajuste.</li>
        <li><strong>Accepter les paliers.</strong> Trois pas en avant, un en arrière. C'est normal, et ce n'est pas une rechute.</li>
      </ol>

      <h2>Adaptations type</h2>
      <AdaptationTable rows={[
        { standard: "Course à pied", adapted: "Marche rapide, vélo, rameur" },
        { standard: "Charges externes (barre, kettlebell)", adapted: "Poids du corps, élastiques, charges très légères" },
        { standard: "Mouvements explosifs (cleans, snatchs)", adapted: "Mouvements lents et contrôlés (deadlift technique, goblet squat)" },
        { standard: "Sauts (box jumps, double-unders)", adapted: "Step-ups, single-unders, sauts au sol contrôlés" },
        { standard: "Inversions (handstand, kipping)", adapted: "Pike push-ups box, ring rows" },
      ]} />

      <h2>Notre rôle de coach</h2>
      <p>Le programme <Link to="/reprendre-le-sport">"Reprendre le sport"</Link> est conçu précisément pour ce contexte. À la première séance, on prend le temps : votre histoire, vos restrictions, vos peurs aussi. On construit un plan, on échange avec votre kiné si vous le souhaitez, et on avance ensemble. Pour un cadre individualisé, le <Link to="/blog/coaching-individuel-objectifs-sportifs">coaching individuel</Link> permet d'aller plus vite et plus précisément.</p>

      <FAQList items={faq} />

      <FurtherReading items={[
        { label: "Reprendre le sport après une blessure (pilier)", to: "/blog/reprendre-le-sport-apres-blessure" },
        { label: "Faire du sport avec une douleur", to: "/blog/faire-du-sport-avec-douleur" },
        { label: "Programme Reprendre le sport", to: "/reprendre-le-sport" },
        { label: "Coaching individuel selon vos objectifs", to: "/blog/coaching-individuel-objectifs-sportifs" },
      ]} />

      <SourcesSection items={[
        { label: "INSERM — Activité physique : prévention et thérapeutique", href: "https://www.inserm.fr/expertise-collective/activite-physique-prevention-traitement-maladies-chroniques/" },
        { label: "BJSM — Return to sport after trauma", href: "https://bjsm.bmj.com/" },
      ]} />
    </ArticleLayout>
  );
}
